import { useState } from "react";
import {
  Key,
  Copy,
  Eye,
  EyeOff,
  Plus,
  Check,
  AlertTriangle,
  Ban,
} from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import Badge from "@/components/common/Badge";
import Card from "@/components/common/Card";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import ActionMenu from "@/components/common/ActionMenu";
import Modal from "@/components/common/Modal";
import Button from "@/components/common/Button";
import RowIcon from "@/components/common/RowIcon";
import { usePageTitle } from "@/components/common/AppShell";

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const tokenStatus = (token) => {
  if (token.revokedAt) return "revoked";
  if (token.expiresAt && new Date(token.expiresAt) < new Date())
    return "expired";
  return "active";
};

const STATUS_VARIANT = {
  active: "success",
  revoked: "danger",
  expired: "neutral",
};

// Modal step 1: create form
function CreateTokenForm({ onSubmit, onCancel, submitting }) {
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit(name.trim(), expiresAt ? new Date(expiresAt).toISOString() : null);
  };

  return (
    <form onSubmit={handleSubmit} id="create-token-form">
      <div className="flex flex-col gap-4">
        <div>
          <label
            htmlFor="token-name"
            className="mb-1.5 block text-[13px] font-semibold text-(--text-primary)"
          >
            Name <span className="text-(--status-danger)">*</span>
          </label>
          <input
            id="token-name"
            type="text"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. CI deploy key"
            className="w-full rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-3 py-2 text-[13px] text-(--text-primary) outline-none placeholder:text-(--text-muted) focus:border-(--brand-primary)"
          />
        </div>
        <div>
          <label
            htmlFor="token-expires"
            className="mb-1.5 block text-[13px] font-semibold text-(--text-primary)"
          >
            Expires at{" "}
            <span className="font-normal text-(--text-muted)">(optional)</span>
          </label>
          <input
            id="token-expires"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-3 py-2 text-[13px] text-(--text-primary) outline-none focus:border-(--brand-primary)"
          />
          <p className="mt-1 text-[12px] text-(--text-muted)">
            Leave blank to create a non-expiring token.
          </p>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="create-token-form"
          disabled={submitting || !name.trim()}
        >
          {submitting ? "Creating…" : "Create"}
        </Button>
      </div>
    </form>
  );
}

// Modal step 2: reveal the one-time token
function TokenReveal({ plaintext, maskedValue, onDone }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(plaintext).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3 rounded-(--radius-md) border border-(--pastel-amber) bg-(--pastel-amber) p-3">
        <AlertTriangle
          size={18}
          className="mt-0.5 shrink-0 text-(--status-warn)"
        />
        <p className="text-[13px] font-semibold text-(--status-warn)">
          This token will NOT be shown again. Copy it now and store it securely.
        </p>
      </div>

      <div>
        <label className="mb-1.5 block text-[13px] font-semibold text-(--text-primary)">
          Your new access token
        </label>
        <div className="flex items-center gap-2 rounded-(--radius-md) border border-(--border-strong) bg-(--surface-2) px-3 py-2">
          <span className="flex-1 break-all font-mono text-[13px] text-(--text-primary)">
            {visible ? plaintext : maskedValue}
          </span>
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            className="shrink-0 border-0 bg-transparent text-(--text-muted) hover:text-(--text-primary) transition-colors"
            aria-label={visible ? "Hide token" : "Show token"}
          >
            {visible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 border-0 bg-transparent text-(--text-muted) hover:text-(--text-primary) transition-colors"
            aria-label="Copy token"
          >
            {copied ? (
              <Check size={16} className="text-(--status-success)" />
            ) : (
              <Copy size={16} />
            )}
          </button>
        </div>
        {copied && (
          <p className="mt-1 text-[12px] text-(--status-success)">
            Copied to clipboard!
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={onDone}>I&apos;ve copied it — Done</Button>
      </div>
    </div>
  );
}

export default function AccessTokensPage() {
  usePageTitle("Access Tokens");
  const {
    tokens,
    tokensLoading,
    tokensError,
    createToken,
    revokeToken,
    currentUserType,
  } = useApp();

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // After creation, hold the one-time plaintext + maskedValue for reveal step
  const [revealData, setRevealData] = useState(null);

  const isAdmin = currentUserType === "admin";

  const handleOpenModal = () => {
    setRevealData(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    // Discard plaintext from state on close
    setRevealData(null);
    setModalOpen(false);
  };

  const handleCreate = async (name, expiresAt) => {
    setSubmitting(true);
    try {
      const created = await createToken(name, expiresAt);
      // Switch the modal to reveal step
      setRevealData({
        plaintext: created.token,
        maskedValue: created.maskedValue,
      });
    } catch (error) {
      window.alert(
        "Failed to create token: " + (error.message || "Unknown error"),
      );
      setModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (uuid, name) => {
    if (
      !window.confirm(
        `Are you sure you want to revoke token "${name}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await revokeToken(uuid);
    } catch (error) {
      window.alert(
        "Failed to revoke token: " + (error.message || "Unknown error"),
      );
    }
  };

  const headerRow = (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <p className="text-base text-(--text-secondary)">
        Manage API access tokens for programmatic access.
      </p>
      <button
        type="button"
        onClick={handleOpenModal}
        className="inline-flex items-center gap-2 rounded-(--radius-md) border-0 bg-(--brand-primary) px-[18px] py-[10px] text-[13px] font-bold text-(--brand-primary-contrast) outline-none transition-colors hover:bg-(--brand-primary-hover) focus:outline-none"
      >
        <Plus size={16} strokeWidth={2.5} />
        Create token
      </button>
    </div>
  );

  if (tokensLoading) {
    return (
      <section>
        {headerRow}
        <Card>
          <p className="text-sm text-(--text-secondary)">Loading tokens…</p>
        </Card>
      </section>
    );
  }

  if (tokensError) {
    return (
      <section>
        {headerRow}
        <Card>
          <p className="text-sm text-(--status-danger)">
            Error loading tokens: {tokensError.message}
          </p>
        </Card>
      </section>
    );
  }

  const colSpan = isAdmin ? 8 : 7;

  return (
    <section>
      {headerRow}

      <DataTable>
        <thead>
          <tr className={DATA_TABLE_CLASSES.headRow}>
            <th className={DATA_TABLE_CLASSES.th}>Name</th>
            {isAdmin && <th className={DATA_TABLE_CLASSES.th}>Owner</th>}
            <th className={DATA_TABLE_CLASSES.th}>Token</th>
            <th className={DATA_TABLE_CLASSES.th}>Created</th>
            <th className={DATA_TABLE_CLASSES.th}>Last used</th>
            <th className={DATA_TABLE_CLASSES.th}>Expires</th>
            <th className={DATA_TABLE_CLASSES.th}>Status</th>
            <th className={DATA_TABLE_CLASSES.thRight}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tokens.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className={DATA_TABLE_CLASSES.emptyCell}>
                No access tokens yet
              </td>
            </tr>
          ) : (
            tokens.map((token) => {
              const status = tokenStatus(token);
              const isRevoked = status === "revoked";
              const menuItems = isRevoked
                ? []
                : [
                    {
                      label: "Revoke",
                      icon: Ban,
                      danger: true,
                      onClick: () => handleRevoke(token.uuid, token.name),
                    },
                  ];
              return (
                <tr key={token.uuid} className={DATA_TABLE_CLASSES.bodyRow}>
                  <td className={DATA_TABLE_CLASSES.td}>
                    <div className="flex items-center gap-3">
                      <RowIcon icon={Key} />
                      <span className="font-semibold">{token.name}</span>
                    </div>
                  </td>
                  {isAdmin && (
                    <td
                      className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                    >
                      {token.username ?? "—"}
                    </td>
                  )}
                  <td
                    className={`${DATA_TABLE_CLASSES.td} font-mono text-(--text-secondary)`}
                  >
                    {token.maskedValue}
                  </td>
                  <td
                    className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                  >
                    {formatDate(token.createdAt)}
                  </td>
                  <td
                    className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                  >
                    {formatDate(token.lastUsedAt)}
                  </td>
                  <td
                    className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                  >
                    {formatDate(token.expiresAt)}
                  </td>
                  <td className={DATA_TABLE_CLASSES.td}>
                    <Badge variant={STATUS_VARIANT[status]}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Badge>
                  </td>
                  <td className={DATA_TABLE_CLASSES.tdRight}>
                    {menuItems.length > 0 ? (
                      <ActionMenu
                        ariaLabel={`Actions for ${token.name}`}
                        items={menuItems}
                      />
                    ) : null}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </DataTable>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={
          revealData ? "Token created — copy it now" : "Create access token"
        }
        size="small"
      >
        {revealData ? (
          <TokenReveal
            plaintext={revealData.plaintext}
            maskedValue={revealData.maskedValue}
            onDone={handleCloseModal}
          />
        ) : (
          <CreateTokenForm
            onSubmit={handleCreate}
            onCancel={handleCloseModal}
            submitting={submitting}
          />
        )}
      </Modal>
    </section>
  );
}
